import { CommonUI, CommonUIEvents, CreateOptions, ShowPreloader } from "@gluckgames/commonui";
import { Setup } from "appworks/boot/setup";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { gameLoop } from "appworks/core/game-loop";
import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { Layers } from "appworks/graphics/layers/layers";
import { loader } from "appworks/loader/loader";
import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { httpConnector } from "appworks/server/connectors/http-connector";
import { Services } from "appworks/services/services";
import { UIFlagState } from "appworks/ui/flags/ui-flag-state";
import { UIFlag, uiFlags, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { UI } from "appworks/ui/ui";
import { Contract } from "appworks/utils/contracts/contract";
import { deviceInfo } from "appworks/utils/device-info";
import { gamevyModel, Modes } from "gamevy/gamevy-model";
import { gamevyHTTPConnector } from "gamevy/server/connectors/gamevy-http-connector";
import { gamevyOfflineHTTPConnector } from "gamevy/server/connectors/gamevy-offline-http-connector";
import { Signal } from "signals";
import { slotModel } from "slotworks/model/slot-model";
import { Jurisdiction } from "slotworks/services/jurisdiction/jurisdiction-service";
import { SlotWorks } from "slotworks/slotworks";
import { BalanceGetCommand } from "./commands/comms/balance-command";
import { CommonUISettings, CUIOptions } from "./components/common-ui-settings";
import { GamevyBalanceResponseSchema } from "./integration/schema/gamevy-balance-response-scheme";
import { GamevyBackendSetup } from "./setup/gamevy-backend-setup";
import { GetGamevyControlFactories } from "./ui/get-gamevy-control-factories";

export class Gamevy {

    public static go(
        setup: Setup | Setup[],
        assetworksData: any,
        commonuiOptions?: CUIOptions,
        setupGamevyAutomatically: boolean = true,
        customGamevySetup?: GamevyBackendSetup
    ) {
        gamevy.options = commonuiOptions;

        canvasManager.enableIpadFullScreen = false;

        gamevyModel.loadURLParams();

        Model.write({ settings: { language: gamevyModel.language } });

        loader.loadSoundsInPreloader = true;

        httpConnector.defaultHeaders.set("X-Gamevy-Session-Token", gamevyModel.sid);
        gamevyHTTPConnector.defaultHeaders.set("X-Gamevy-Session-Token", gamevyModel.sid);
        gamevyOfflineHTTPConnector.defaultHeaders.set("X-Gamevy-Session-Token", gamevyModel.sid);

        gamevy.createCommonUI(commonuiOptions);

        gamevy.setupGamevyGameBeforeSlotworks(commonuiOptions);

        gamevy.setupFullscreenCapabilities();

        if (gamevyModel.gameName.length === 0) {
            throw new Error("No game name specified for Gamevy.");
        }

        const setups: Setup[] = (Array.isArray(setup)) ? setup : [setup];
        if (setupGamevyAutomatically) {
            SlotWorks.go([customGamevySetup ?? new GamevyBackendSetup(), ...setups], assetworksData);
        } else {
            SlotWorks.go(setups, assetworksData);
        }

        gamevy.setupListeners();

        gamevy.setupJurisdictions();
    }

    public COMMON_UI_FIT_TO_CANVAS: boolean = true;
    public PREPARE_RULES_URL: boolean = true;

    public MANUAL_GAME_ROUNDS: boolean = false;
    public commonUI: CommonUI;
    public commonUISettings: CommonUISettings;
    public options: CUIOptions;
    public gameInstanceIDUpdated: Signal = new Signal();

    public betPromoInfo: any | undefined;
    public balancePromoInfo: any | undefined;
    public balanceData?: GamevyBalanceResponseSchema;

    private gameInstanceID: number = -1;

    public onReady(): void {
        if (this.PREPARE_RULES_URL) {
            gamevyModel.prepareRulesUrl(gamevyModel.language).then(() => this.setCustomRulesIFrame());
        }
        Services.bet.setFixedBetCost(0);

        this.commonUISettings = new CommonUISettings();

        this.setupGamevyGameAfterSlotworks();

        if (gamevyModel.mode === Modes.FUN) {
            gameState.newGame();
            this.updateBalance(gamevyModel.initialBalance || 200000);
        }
    }

    public showPreloader(data?: ShowPreloader): void {
        this.commonUI.showPreloader(data);
    }

    public gameRoundStart(): void {
        this.commonUI.gameRoundStarted({
            gameInstanceId: this.getGameInstanceID(),
            bet: {
                amount: Services.bet.getTotalStake()
            }
        });
    }

    public gameRoundEnd(): void {
        this.commonUI.gameRoundEnded();
    }

    public gameRoundAnimationsComplete(): void {
        this.commonUI.gameRoundEndedAndAnimationsDone();
    }

    public setGameInstanceID(id: number): void {
        gameState.getCurrentGame().id = id.toString();

        this.gameInstanceID = id;
        if (this.commonUI) {
            this.commonUI.updateGameInstanceId({ id });
        }

        if (!this.MANUAL_GAME_ROUNDS) {
            this.gameRoundStart();
        }

        this.gameInstanceIDUpdated.dispatch(this.gameInstanceID);
    }

    public getGameInstanceID(): number {
        return this.gameInstanceID;
    }

    public processBalanceData(): void {
        const bet: number = Services.bet.getTotalStake();

        let bonusDefecit = bet;
        if (this.balanceData?.BONUS !== undefined) {
            const bonus = this.balanceData.BONUS - bet;
            bonusDefecit = (bonus < 0) ? bonus : 0;
            this.balanceData.BONUS = (bonus > 0) ? bonus : 0;
        }

        if (this.balanceData?.REAL !== undefined) {
            const real = this.balanceData.REAL - Math.abs(bonusDefecit);
            this.balanceData.REAL = (real > 0) ? real : 0;
        }
    }

    public userWantsToBet(): Contract<void> {
        return new Contract((resolve) => {
            gamevy.commonUI.userWantsToBet({ bet: { amount: Services.bet.getTotalStake() } }).then((promo: any) => {
                this.betPromoInfo = promo;
                resolve();
            });
        });
    }

    public hideCommonUI(): void {
        this.commonUI.visible = false;
        document.getElementById("commonui").style.display = "none";
    }

    public showCommonUI(): void {
        this.commonUI.visible = true;
        document.getElementById("commonui").style.display = "block";
    }

    public preloadingFinished(): void {
        this.commonUI.hidePreloader();
        if (this.balanceData) {
            this.commonUI.setConnectorData({
                bonusAmount: this.balanceData.BONUS,
                realAmount: this.balanceData.REAL,
                promoInfo: this.balanceData.promoInfo
            });
        }
        this.commonUI.gameLoadingEnded();
        this.showCommonUI();
    }

    public showMenu(defaultTab: string): Contract<void> {
        return new Contract<void>((resolve) => {
            const promptOption = canvasManager.iosScrollPromptEnabled;
            canvasManager.iosScrollPromptEnabled = false;
            this.commonUI.showMenu(defaultTab).then(() => {
                canvasManager.iosScrollPromptEnabled = promptOption;
                this.setupFullscreenCapabilities();
                resolve();
            });
        });
    }

    public setupJurisdictions(): void {
        Services.jurisdiction.setJurisdiction(gamevyModel.jurisdiction);

        if (gamevyModel.jurisdiction === Jurisdiction.Alderney || (gamevyModel.jurisdiction === Jurisdiction.UK && gamevyModel.operator === "daub")) {
            Services.jurisdiction.setMinPreloaderTime(5000);
            Services.jurisdiction.setPreloaderMessageKey("preloader_ad");
        }
    }

    public trackBetChanges(): void {
        let currentBet: number = Services.bet.getTotalStake();

        slotModel.onUpdate.add(() => {
            const newBet: number = Services.bet.getTotalStake();
            gamevy.commonUI.updateBet({ amount: newBet });
            if (currentBet !== newBet) {
                gamevy.commonUI.updateWin({ amount: 0 });
            }
            currentBet = newBet;
        });
    }

    private createCommonUI(commonui?: CUIOptions): void {
        const parent = document.getElementById("commonui");
        if (this.COMMON_UI_FIT_TO_CANVAS) {
            canvasManager.extraCanvasContainers.push(parent);
        }
        if (commonui.customLayout) {
            canvasManager.scaledContainers.push(parent);
        }

        const options: CreateOptions = {
            noMenu: false,
            noMenuButton: false,
            noBottomBar: this.options.noBottomBar,
            noDialogs: false,
            noPreloader: false,
            noThirdParty: false,
            noThirdPartyDialogs: false,
            languageCode: gamevyModel.language,
            currencyCode: gamevyModel.currency,
            platform: gamevyModel.platform,
            operator: gamevyModel.operator,
            urlParams: gamevyModel.urlParams,
            realityCheckOptions: gamevyModel.getRealityCheckOptions(),
            element: parent
        };

        if (commonui) {
            for (const key in commonui) {
                if (key) {
                    options[key] = commonui[key];
                }
            }
        }

        this.commonUI = CommonUI.create(options);

        if (options.noPreloader) {
            this.hideCommonUI();
        }
    }

    private setupGamevyControls(): void {
        UI.factories.push(...GetGamevyControlFactories());

        if (Layers.get("Speed")?.hasScene("default")) {
            const disableFlags =
                UIFlag.FREE_SPINS |
                UIFlag.PROMPT |
                UIMenuFlag.MENU |
                UIFlag.GAMBLE_UI |
                UIFlag.BONUS;
            UI.layer(Layers.get("Speed"), [{
                scene: "default",
                enable: new UIFlagState(0, disableFlags)
            }]);
        }
    }

    private setupGamevyGameBeforeSlotworks(commonui?: any): void {
        PromptComponent.ENTER_TO_CONTINUE = true;

        this.commonUI.updateGameId({ gameId: gamevyModel.gameID });
        this.commonUI.gameLoadingStarted();
    }

    private setupFullscreenCapabilities(): void {
        if (gamevyModel.useGcm || !deviceInfo.isUltraWide()) {
            canvasManager.iosScrollPromptEnabled = false;
        }
    }

    private setupGamevyGameAfterSlotworks(): void {
        this.setupGamevyControls();
    }

    private setupListeners(): void {
        this.commonUI.on(CommonUIEvents.PAUSE_GAME, () => this.pauseGame());
        this.commonUI.on(CommonUIEvents.UNPAUSE_GAME, () => this.unpauseGame());

        this.commonUI.on(CommonUIEvents.BALANCE_UPDATED, (balance: number) => this.updateBalance(balance));
        this.commonUI.on(CommonUIEvents.REQUEST_BALANCE_UPDATE, () => this.requestBalanceUpdate());

        this.commonUI.on(CommonUIEvents.MUTE_GAME_EFFECTS, () => this.muteGameEffects());
        this.commonUI.on(CommonUIEvents.UNMUTE_GAME_EFFECTS, () => this.unmuteGameEffects());
        this.commonUI.on(CommonUIEvents.MUTE_GAME_BACKGROUND, () => this.muteGameBackground());
        this.commonUI.on(CommonUIEvents.UNMUTE_GAME_BACKGROUND, () => this.unmuteGameBackground());

        this.commonUI.on(CommonUIEvents.MENU_BUTTON_CLICKED, () => this.menuButtonClicked());

        this.commonUI.on(CommonUIEvents.STOP_AUTOSPIN, () => this.stopAutospin());

        this.commonUI.on(CommonUIEvents.DISABLE_BET_OPTIONS, () => this.enableBetOptions(false));
        this.commonUI.on(CommonUIEvents.ENABLE_BET_OPTIONS, () => this.enableBetOptions(true));
        this.commonUI.on(CommonUIEvents.SET_DEFAULT_BET, () => this.setDefaultBet());

        Services.sound.onMuteChange.add((muted) => this.onSoundSettingsChanged(muted));
    }

    private enableBetOptions(enabled: boolean): void {
        uiFlags.set(UIFlag.PROMPT, !enabled);
    }

    private setDefaultBet(): void {
        slotModel.write({ bet: Services.bet.getCreditsForTotalStake(slotModel.read().bet.creditSizes.defaultValue, false) });

        gamevy.commonUI.updateBet({ amount: Services.bet.getTotalStake() });
        gamevy.commonUI.updateWin({ amount: 0 });
    }

    private pauseGame(): void {
        gameLoop.setPaused("gamevy", true);
        Services.autoplay.stop();
        uiFlags.set(UIFlag.EXTERNAL_DISABLE, true);
    }

    private unpauseGame(): void {
        gameLoop.setPaused("gamevy", false);
        uiFlags.set(UIFlag.EXTERNAL_DISABLE, false);
    }

    private updateBalance(balance: number): void {
        Services.transaction.setBalance(balance);
        gameState.getCurrentGame().balance = balance;
    }

    private requestBalanceUpdate(): void {
        BalanceGetCommand().then(() => {
            Services.transaction.setBalance(gameState.getCurrentGame().balance);
        });
    }

    private onSoundSettingsChanged(muted: boolean): void {
        this.commonUI.soundSettingsChanged("background", !muted);
        this.commonUI.soundSettingsChanged("effects", !muted);
    }

    private muteGameEffects(): void {
        Services.sound.setSoundFxMute(true);
    }

    private unmuteGameEffects(): void {
        Services.sound.setSoundFxMute(false);
    }

    private muteGameBackground(): void {
        Services.sound.setMusicMute(true);
    }

    private unmuteGameBackground(): void {
        Services.sound.setMusicMute(false);
    }

    private stopAutospin(): void {
        Services.autoplay.stop();
    }

    private menuButtonClicked(): void {
        if (uiFlags.has(UIFlag.IDLE)) {
            this.commonUI.setMenuSettings(this.commonUISettings.getHamburgerSettings());
            this.showMenu("sound").execute();
        }
    }

    private setCustomRulesIFrame() {
        const iFrame: HTMLIFrameElement = document.getElementById("epic_rules_iframe") as HTMLIFrameElement;
        if (iFrame) {
            iFrame.src = gamevyModel.getRulesUrl();
        }
    }
}

export const gamevy = new Gamevy();
